import type { ApiTokenScope } from '../types/enums.js';

/**
 * Reason an API token was rejected. Derived from the exact messages the backend
 * emits in `api-tokens.service.ts` / `guards/api-token.guard.ts`.
 */
export type AuthFailureReason =
  | 'missing_bearer'
  | 'invalid_format'
  | 'invalid_token'
  | 'token_inactive'
  | 'token_expired'
  | 'user_inactive'
  | 'unknown';

/** Why a 403 came back. See {@link GpmPayPermissionError}. */
export type PermissionFailureReason = 'scope' | 'endpoint' | 'ownership';

export type ConfigErrorCode =
  | 'missing_api_token'
  | 'invalid_api_token'
  | 'invalid_api_token_format'
  | 'invalid_base_url'
  | 'invalid_argument';

export type WebhookSignatureFailureReason =
  | 'missing_secret'
  | 'malformed_header'
  | 'timestamp_skew'
  | 'mismatch';

const BRAND = Symbol.for('gpmpay.sdk.error');

/**
 * Base class for everything this SDK throws.
 *
 * Generic over `code` so subclasses can narrow it (and give consumers
 * autocomplete on `error.code`) without redeclaring the field.
 */
export class GpmPayError<TCode extends string = string> extends Error {
  readonly code: TCode;
  /** @internal Cross-realm brand — `instanceof` breaks across CJS/ESM copies. */
  readonly [BRAND] = true as const;

  constructor(message: string, code: TCode) {
    super(message);
    this.name = new.target.name;
    this.code = code;
    Object.setPrototypeOf(this, new.target.prototype);
  }

  /**
   * Prefer this over `instanceof` when a dual CJS/ESM install could put two
   * copies of the class in one process.
   */
  static isGpmPayError(value: unknown): value is GpmPayError {
    return (
      typeof value === 'object' &&
      value !== null &&
      (value as Record<symbol, unknown>)[BRAND] === true
    );
  }
}

/** Bad SDK usage — thrown before any network call happens. */
export class GpmPayConfigError extends GpmPayError<ConfigErrorCode> {
  constructor(message: string, code: ConfigErrorCode = 'invalid_argument') {
    super(message, code);
  }
}

/** DNS/TCP/TLS failure. `cause` holds the original error. */
export class GpmPayConnectionError extends GpmPayError {
  /** Overrides the standard `Error.cause` so it is always populated here. */
  override readonly cause: unknown;
  /** Node's `err.cause.code`, e.g. `ECONNREFUSED`, `ENOTFOUND`. */
  readonly syscallCode: string | undefined;

  constructor(message: string, cause: unknown, syscallCode?: string) {
    super(message, 'connection_error');
    this.cause = cause;
    this.syscallCode = syscallCode;
  }
}

export class GpmPayTimeoutError extends GpmPayError {
  readonly timeoutMs: number;

  constructor(message: string, timeoutMs: number) {
    super(message, 'timeout');
    this.timeoutMs = timeoutMs;
  }
}

export class GpmPayWebhookSignatureError extends GpmPayError {
  readonly reason: WebhookSignatureFailureReason;

  constructor(message: string, reason: WebhookSignatureFailureReason) {
    super(message, 'webhook_signature');
    this.reason = reason;
  }
}

export interface ApiErrorContext {
  status: number;
  requestId: string;
  rawBody: unknown;
  rawMessage: string | string[];
}

/** Any non-2xx HTTP response. */
export class GpmPayAPIError extends GpmPayError {
  readonly status: number;
  /** Correlation id sent as `X-GPMPay-Request-Id`. Quote it to support. */
  readonly requestId: string;
  readonly rawBody: unknown;
  readonly rawMessage: string | string[];

  constructor(message: string, ctx: ApiErrorContext, code = 'api_error') {
    super(message, code);
    this.status = ctx.status;
    this.requestId = ctx.requestId;
    this.rawBody = ctx.rawBody;
    this.rawMessage = ctx.rawMessage;
  }
}

/** 400 — request body failed validation. */
export class GpmPayBadRequestError extends GpmPayAPIError {
  /** One entry per failed constraint, as produced by Nest's ValidationPipe. */
  readonly validationMessages: string[];

  constructor(
    message: string,
    ctx: ApiErrorContext & { validationMessages: string[] },
  ) {
    super(message, ctx, 'bad_request');
    this.validationMessages = ctx.validationMessages;
  }
}

/** 401 — the API token was rejected. */
export class GpmPayAuthenticationError extends GpmPayAPIError {
  readonly reason: AuthFailureReason;

  constructor(message: string, ctx: ApiErrorContext & { reason: AuthFailureReason }) {
    super(message, ctx, 'authentication_error');
    this.reason = ctx.reason;
  }
}

/**
 * 403 — one of three things:
 *
 * - `scope` — the token exists but lacks the scope this route requires.
 * - `endpoint` — the route accepts no API token at all (dashboard-only).
 * - `ownership` — the resource exists but belongs to another account.
 */
export class GpmPayPermissionError extends GpmPayAPIError {
  readonly missingScope: ApiTokenScope | undefined;
  readonly reason: PermissionFailureReason;

  constructor(
    message: string,
    ctx: ApiErrorContext & {
      missingScope?: ApiTokenScope;
      reason: PermissionFailureReason;
    },
  ) {
    super(message, ctx, 'permission_error');
    this.missingScope = ctx.missingScope;
    this.reason = ctx.reason;
  }
}

export class GpmPayNotFoundError extends GpmPayAPIError {
  /** Resource name parsed out of e.g. `"Order not found"`. */
  readonly resource: string | undefined;

  constructor(message: string, ctx: ApiErrorContext & { resource?: string }) {
    super(message, ctx, 'not_found');
    this.resource = ctx.resource;
  }
}

export class GpmPayRateLimitError extends GpmPayAPIError {
  readonly retryAfterSeconds: number | undefined;

  constructor(
    message: string,
    ctx: ApiErrorContext & { retryAfterSeconds?: number },
  ) {
    super(message, ctx, 'rate_limit');
    this.retryAfterSeconds = ctx.retryAfterSeconds;
  }
}

export class GpmPayServerError extends GpmPayAPIError {
  constructor(message: string, ctx: ApiErrorContext) {
    super(message, ctx, 'server_error');
  }
}

/** Ordered most-specific first — `Invalid token` must lose to `Invalid token format`. */
const AUTH_REASONS: [RegExp, AuthFailureReason][] = [
  [/missing bearer token/i, 'missing_bearer'],
  [/invalid token format/i, 'invalid_format'],
  [/token is not active/i, 'token_inactive'],
  [/token[_ ]expired/i, 'token_expired'],
  [/user inactive/i, 'user_inactive'],
  [/invalid token/i, 'invalid_token'],
];

function extractMessage(body: unknown): string | string[] {
  if (typeof body === 'string' && body.trim() !== '') return body;
  if (body !== null && typeof body === 'object') {
    const b = body as { message?: unknown; error?: unknown };
    if (Array.isArray(b.message) || typeof b.message === 'string') {
      return b.message as string | string[];
    }
    if (typeof b.error === 'string') return b.error;
  }
  return '';
}

/**
 * Map an HTTP error response onto the typed error hierarchy.
 *
 * @remarks
 * The backend registers no global exception filter, so error bodies use Nest's
 * default shape (`{ statusCode, message, error }`) and are NOT wrapped in the
 * `{ statusCode, message, data }` success envelope.
 */
export function errorFromResponse(
  status: number,
  body: unknown,
  requestId: string,
  extra: { retryAfterSeconds?: number } = {},
): GpmPayAPIError {
  const raw = extractMessage(body);
  const msg =
    (Array.isArray(raw) ? raw.join('; ') : raw) || `HTTP ${String(status)}`;
  const ctx: ApiErrorContext = {
    status,
    requestId,
    rawBody: body,
    rawMessage: raw === '' ? msg : raw,
  };

  switch (status) {
    case 400:
      return new GpmPayBadRequestError(msg, {
        ...ctx,
        validationMessages: Array.isArray(raw) ? raw : [msg],
      });

    case 401: {
      const reason =
        AUTH_REASONS.find(([re]) => re.test(msg))?.[1] ?? 'unknown';
      return new GpmPayAuthenticationError(
        `${msg} — check your API token is correct, ACTIVE and not expired.`,
        { ...ctx, reason },
      );
    }

    case 403: {
      const scope = /missing scope:\s*(\S+)/i.exec(msg)?.[1];
      if (scope) {
        return new GpmPayPermissionError(
          `API token is missing the "${scope}" scope. Regenerate the token with that scope enabled.`,
          { ...ctx, missingScope: scope as ApiTokenScope, reason: 'scope' },
        );
      }
      // `ApiTokenGuard` is fail-closed: a route that declares no `@ApiScopes()`
      // rejects API tokens outright, whoever owns the resource. Reporting that
      // as an ownership problem sends people hunting for the wrong bug.
      if (/not available to api tokens/i.test(msg)) {
        return new GpmPayPermissionError(
          `${msg} — this endpoint is dashboard-only and no API token scope grants it.`,
          { ...ctx, reason: 'endpoint' },
        );
      }
      return new GpmPayPermissionError(
        `${msg} — the resource exists but belongs to another account.`,
        { ...ctx, reason: 'ownership' },
      );
    }

    case 404: {
      const resource = /^(.*?)\s+not found/i.exec(msg)?.[1];
      return new GpmPayNotFoundError(
        msg,
        resource === undefined ? ctx : { ...ctx, resource },
      );
    }

    // No 409 case: every route this SDK can reach is either a read or a write
    // with no uniqueness constraint, so a conflict has no source. A 409 from
    // `client.request()` falls through to the generic GpmPayAPIError below.

    case 429:
      return new GpmPayRateLimitError(
        msg,
        extra.retryAfterSeconds === undefined
          ? ctx
          : { ...ctx, retryAfterSeconds: extra.retryAfterSeconds },
      );

    default:
      return status >= 500
        ? new GpmPayServerError(msg, ctx)
        : new GpmPayAPIError(msg, ctx);
  }
}
